import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { fetchContacts, searchContacts, deleteContacts } from "../reducers/asyncThunks";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableColumnHeaderCell,
    DataTableContent,
    DataTableHeaderRow,
    DataTableRow,
    TextButton,
    List,
    ListItem,
    SortDirection,
    DataTablePagination,
    DataTableHeaderCheckboxCell,
    DataTableCheckboxCell,
    Search,
    DataTableToolbar,
    Typography,
    Dialog,
    DataTableOverflowMenuCell,
    DataTableColumnManageButton,
    Checkbox,
} from "@episerver/ui-framework";
import { Contact, ServerSortType, ContactsType } from "../model/contact";
import {
    selectContact,
    deselectContact,
    sort,
    selectAllContacts,
    deselectAllContacts,
    onChangeConfirmDialog,
    initialContactState,
    reset
} from "../reducers/contactListReducer";
import { useHistory } from "react-router-dom";
import { debounce } from "lodash-es";
import { RootState } from "../../store";
import { readOnlySelector } from "../../AdminInfo/adminInfoReducer";

interface ContactListProps {
    type: ContactsType;
}

const ContactList = (props: ContactListProps) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const {
        contacts,
        selectedContacts,
        contactListPagination: { rowsPerPage, startRow, rowsPerPageOptions, totalRowCount, searchInput },
        sort: { sortDirection, sortedColumn },
        confirmDialog,
    } = useAppSelector((state: RootState) => state.contactList);
    const readOnly = useAppSelector(readOnlySelector);

    const sortDirectionRef = useRef<SortDirection>(sortDirection);
    const sortedColumnRef = useRef<string>(sortedColumn);

    let columnsDefault = props.type === ContactsType.Accounts  ? {
        fullName: { selector: "FullName", name: "Full Name", sortable: true, visible: true },
        code: { selector: "Code", name: "Account Code", sortable: true, visible: true },
        salesOrganization: { selector: "SalesOrganization", name: "Sales Organization", sortable: true, visible: true },
        priceGroup: { selector: "PriceGroup", name: "Price Group", sortable: true, visible: true },
        distributionChannel: { selector: "DistributionChannel", name: "Distribution Channel", sortable: true, visible: true },
        allowWebOrder: { selector: "AllowWebOrder", name: "Allow Web Order", sortable: true, visible: true },
        sapCustomerGroup: { selector: "SAPCustomerGroup", name: "SAP Customer Group", sortable: true, visible: true },
    } : {
        fullName: { selector: "FullName", name: "Full Name", sortable: true, visible: true },
        firstName: { selector: "FirstName", name: "First Name", sortable: true, visible: true },
        lastName: { selector: "LastName", name: "Last Name", sortable: true, visible: true },
        customerGroup: { selector: "CustomerGroup", name: "Customer Group", sortable: true, visible: true },
        email: { selector: "Email", name: "Email", sortable: true, visible: true },
    };

    const [columns, setColumns] = useState<
        Record<string, { selector: string; name: string; sortable: boolean; visible: boolean }>
    >(columnsDefault);

    const onToggleAllSelected = (checked: boolean): void => {
        if (checked) dispatch(selectAllContacts());
        else dispatch(deselectAllContacts());
    };

    const isSelected = (row: Contact): boolean => {
        return selectedContacts.includes(row.primaryKeyId!);
    };

    const handleSearch = (value: string, recordsToRetrieve: number) => debouncedSearch(value, recordsToRetrieve);
    const debouncedSearch = useCallback(
        debounce((nextValue: string, recordsToRetrieve: number) => onSearch(nextValue, recordsToRetrieve), 1000),
        []
    );
    const onSearch = (value: string, recordsToRetrieve: number): void => {
        let keyWord = value.trim();
        dispatch(
            searchContacts({
                query: keyWord,
                sortField: sortedColumnRef.current,
                sortType:
                    sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: 0,
                recordsToRetrieve: recordsToRetrieve,
                type: props.type
            })
        );
    };

    const onSort = (column: string, direction: SortDirection): void => {
        dispatch(sort({ column, direction }));
        if (searchInput) {
            dispatch(
                searchContacts({
                    query: searchInput,
                    sortField: column,
                    sortType: direction === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: 0,
                    recordsToRetrieve: rowsPerPage,
                    type: props.type
                })
            );
        } else {
            dispatch(
                fetchContacts({
                    sortField: column,
                    sortType: direction === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: 0,
                    recordsToRetrieve: rowsPerPage,
                    type: props.type
                })
            );
        }
    };

    const onCreateNew = (): void => {
        history.push({
            pathname: `/${ContactsType[props.type]}/Edit`,
        });
    };

    const onEdit = (row: Contact): void => {
        history.push({
            pathname: `/${ContactsType[props.type]}/Edit/` + row.primaryKeyId,
        });
    };

    const onConfirmDialog = (contactIds: string[]): void => {
        let message = `Are you sure to delete these contacts?`;
        dispatch(onChangeConfirmDialog({ ...confirmDialog, open: true, contactIds: contactIds, message: message }));
    };

    const onSelectAll = (e: any): void => {
        if (e != undefined && e.target != undefined) {
            onToggleAllSelected(e.target.checked);
        } else {
            onToggleAllSelected(selectedContacts.length < rowsPerPage);
        }
    };

    const onDelete = (): void => {
        dispatch(
            deleteContacts({
                contactIds: confirmDialog.contactIds,
                query: searchInput,
                sortField: sortedColumn,
                sortType: sortDirection === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: startRow,
                recordsToRetrieve: rowsPerPage,
                type: props.type
            })
        );

        dispatch(onChangeConfirmDialog(initialContactState.confirmDialog));
    };

    const onCancel = (): void => {
        dispatch(onChangeConfirmDialog(initialContactState.confirmDialog));
    };

    const onSelectedRowChanged = (row: Contact, checked: boolean): void => {
        if (checked) dispatch(selectContact(row.primaryKeyId));
        else dispatch(deselectContact(row.primaryKeyId));
    };

    const onChangePage = (startRow: number): void => {
        if (searchInput === "" || searchInput === undefined) {
            dispatch(
                fetchContacts({
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    type: props.type
                })
            );
        } else {
            dispatch(
                searchContacts({
                    query: searchInput,
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    type: props.type
                })
            );
        }
    };

    const onChangeRowsPerPage = (rowsPerPage: number): void => {
        if (searchInput === "" || searchInput === undefined) {
            dispatch(
                fetchContacts({
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: 0,
                    recordsToRetrieve: rowsPerPage,
                    type: props.type
                })
            );
        } else {
            dispatch(
                searchContacts({
                    query: searchInput,
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: 0,
                    recordsToRetrieve: rowsPerPage,
                    type: props.type
                })
            );
        }
    };

    useEffect(() => {
        dispatch(
            fetchContacts({
                sortField: sortedColumn,
                sortType: sortDirection === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: startRow,
                recordsToRetrieve: rowsPerPage,
                type: props.type
            })
        );
        return () => {
            dispatch(reset());
        };
    }, []);

    useEffect(() => {
        sortDirectionRef.current = sortDirection;
    }, [sortDirection]);

    useEffect(() => {
        sortedColumnRef.current = sortedColumn;
    }, [sortedColumn]);

    const renderRow = (rowData: Contact): React.ReactElement => {
        return (
            <DataTableRow rowId={rowData.primaryKeyId!} key={rowData.primaryKeyId} selected={isSelected(rowData)}>
                    <DataTableCheckboxCell
                        disabled={readOnly}
                        defaultChecked={isSelected(rowData)}
                        onChange={(e) => !readOnly && onSelectedRowChanged(rowData, e.target.checked)}
                    />
                <>
                    {columns["fullName"].visible && (
                        <DataTableCell>
                            <a
                                href=""
                                className="underline-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onEdit(rowData);
                                }}
                            >
                                {rowData.fullName}
                            </a>
                        </DataTableCell>
                    )}
 
                    {props.type !== ContactsType.Accounts && columns["firstName"].visible && <DataTableCell>{rowData.firstName ?? ""}</DataTableCell>}
                    {props.type !== ContactsType.Accounts && columns["lastName"].visible && <DataTableCell>{rowData.lastName ?? ""}</DataTableCell>}
                    {props.type !== ContactsType.Accounts && columns["customerGroup"].visible && <DataTableCell>{rowData.customerGroup ?? ""}</DataTableCell>}
                    {props.type !== ContactsType.Accounts && columns["email"].visible && <DataTableCell>{rowData.email ?? ""}</DataTableCell>}
                    {props.type == ContactsType.Accounts && columns["code"].visible && <DataTableCell>{rowData.extendedProperties?.find(property => property.name === columns["code"].selector)?.value ?? ""}</DataTableCell>}
                    {props.type == ContactsType.Accounts && columns["salesOrganization"].visible && <DataTableCell>{rowData.extendedProperties?.find(property => property.name === columns["salesOrganization"].selector)?.value ?? ""}</DataTableCell>}
                    {props.type == ContactsType.Accounts && columns["priceGroup"].visible && <DataTableCell>{rowData.extendedProperties?.find(property => property.name === columns["priceGroup"].selector)?.value ?? ""}</DataTableCell>}
                    {props.type == ContactsType.Accounts && columns["distributionChannel"].visible && <DataTableCell>{rowData.extendedProperties?.find(property => property.name === columns["distributionChannel"].selector)?.value ?? ""}</DataTableCell>}
                    {props.type == ContactsType.Accounts && columns["allowWebOrder"].visible && <DataTableCell><Checkbox disabled={true} checked={rowData.extendedProperties?.find(property => property.name === columns["allowWebOrder"].selector)?.value}/></DataTableCell>}
                    {props.type == ContactsType.Accounts && columns["sapCustomerGroup"].visible && <DataTableCell>{rowData.extendedProperties?.find(property => property.name === columns["sapCustomerGroup"].selector)?.value ?? ""}</DataTableCell>}
                   
                </>
                <DataTableOverflowMenuCell>
                    <List>
                        <ListItem onClick={() => onEdit(rowData)}>{readOnly ? "View" : "Edit"}</ListItem>
                        {!readOnly &&
                            <ListItem onClick={() => onConfirmDialog([rowData.primaryKeyId!])}>Delete</ListItem>
                        }
                    </List>
                </DataTableOverflowMenuCell>
            </DataTableRow>
        );
    };

    return (
        <div className="customer-list">
            <Typography use="headline1">{ContactsType[props.type]}</Typography>
            <DataTable>
                <DataTableToolbar className="customer-list__toolbar">
                    <React.Fragment>
                        <div>
                            {!readOnly && 
                                <React.Fragment>
                                    <TextButton onClick={onCreateNew}>Create</TextButton>
                                    {selectedContacts.length > 0 && (
                                        <TextButton onClick={() => onConfirmDialog(selectedContacts)}>
                                            Delete Selected ({selectedContacts.length})
                                        </TextButton>
                                    )}
                                </React.Fragment>
                            }
                        </div>
                        <Search
                            value={searchInput}
                            onValueChange={(value: string) => {
                                handleSearch(value, rowsPerPage);
                            }}
                            onSearch={(value: string) => {
                                handleSearch(value, rowsPerPage);
                            }}
                        />
                    </React.Fragment>
                </DataTableToolbar>
                <DataTableContent>
                    <DataTableHeaderRow>
                        <DataTableHeaderCheckboxCell readOnly={readOnly} className={"w60"} onClick={(e) => !readOnly && onSelectAll(e)} />
                        <React.Fragment>
                            {Object.keys(columns).map((column) => {
                                if (columns[column].sortable) {
                                    return (
                                        columns[column].visible && (
                                            <DataTableColumnHeaderCell
                                                key={columns[column].selector}
                                                onSort={(_header: string, direction: SortDirection) =>
                                                    onSort(columns[column].selector, direction)
                                                }
                                                sortDirection={
                                                    sortedColumn === columns[column].selector
                                                        ? sortDirection
                                                        : SortDirection.Unsorted
                                                }
                                            >
                                                {columns[column].name}
                                            </DataTableColumnHeaderCell>
                                        )
                                    );
                                } else {
                                    return (
                                        columns[column].visible && (
                                            <DataTableColumnHeaderCell key={columns[column].selector}>
                                                {columns[column].name}
                                            </DataTableColumnHeaderCell>
                                        )
                                    );
                                }
                            })}
                            <DataTableColumnManageButton
                                className={"w57"}
                                columns={Object.values(columns)}
                                onSelectChange={(columnName, checked) => {
                                    const changedColumn = Object.keys(columns).find(
                                        (column) => columns[column].name === columnName
                                    );
                                    if (changedColumn) {
                                        setColumns({
                                            ...columns,
                                            [changedColumn]: { ...columns[changedColumn], visible: checked },
                                        });
                                    }
                                }}
                            />
                        </React.Fragment>
                    </DataTableHeaderRow>
                    <DataTableBody>
                        <>
                            {contacts.length == 0 && (
                                <DataTableRow rowId={"0"}>
                                    <Typography use="body1">No Contacts found.</Typography>
                                </DataTableRow>
                            )}
                            {contacts.map((row: Contact) => renderRow(row))}
                        </>
                    </DataTableBody>
                </DataTableContent>
                <DataTablePagination
                    startRow={startRow}
                    totalRowCount={totalRowCount}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onChangePage={onChangePage}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                />
            </DataTable>
            <Dialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                dismissLabel="Cancel"
                confirmLabel="Delete"
                enableConfirm
                onInteraction={(e) => {
                    if (e.detail.action === "confirm") onDelete();
                    else onCancel();
                }}
            >
                <React.Fragment>
                    <Typography use="body2">{confirmDialog.message}</Typography>
                </React.Fragment>
            </Dialog>
        </div>
    );
};

export default ContactList;
